import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import Business from "@mui/icons-material/Business";
import AirlineSeatReclineExtra from "@mui/icons-material/AirlineSeatReclineExtra";
import DriveEta from "@mui/icons-material/DriveEta";
import Room from "@mui/icons-material/Room";

import BoxInfo from "components/BoxInfo";
import CompanyInfo from "components/CompanyInfo/CompanyInfo";
import appStoreLogo from "~/assets/app_store.png";
import googlePlayLogo from "~/assets/google_play.png";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 0,
    padding: 64,
    paddingBottom: 32,
    color: theme.palette.text.light,
  },
  titleContainer: {
    paddingBottom: 128,
  },
  subtitleContainer: {
    paddingLeft: 32,
  },
  countsContainer: {
    backgroundColor: "rgba(0,0,0,0.5)",
    padding: 96,
    color: theme.palette.text.light,
  },
}));

const Start = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { myAnalytics, users, myCompany } = props;

  const getCompanyStart = () => {
    return (
      <Fragment>
        <div className={classes.root}>
          <Stack
            direction="column"
            className={classes.titleContainer}
            sx={{ justifyContent: "flex-start", alignItems: "center" }}
          >
            <Typography variant="h2">
              {t("Tervetuloa OpenTaxi palveluun!")}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            className={classes.subtitleContainer}
            sx={{ justifyContent: "flex-start", alignItems: "center", flexWrap: "wrap" }}
          >
            <Typography variant="h4">
              {t("Pikalinkit suosikki toimintoihin")}
            </Typography>
          </Stack>

          <Grid container spacing={3} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }} sx={{ p: 2.5 }}>
              <BoxInfo
                icon={<Business />}
                text={t("Tarkasta ja täydennä yrityksesi tiedot")}
                link={"/dashboard/company/edit"}
                status={myCompany?.valid == true ? 1 : 2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }} sx={{ p: 2.5 }}>
              <BoxInfo
                icon={<AirlineSeatReclineExtra />}
                text={t("Hallitse kuljettajia")}
                link={"/dashboard/drivers"}
                status={myAnalytics?.drivers > 0 ? 1 : 2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }} sx={{ p: 2.5 }}>
              <BoxInfo
                icon={<DriveEta />}
                text={t("Hallitse ajoneuvoja")}
                link={"/dashboard/vehicles"}
                status={myAnalytics?.vehicles > 0 ? 1 : 2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }} sx={{ p: 2.5 }}>
              <BoxInfo
                icon={<Room />}
                text={t("Ajohistoria")}
                link={"/dashboard/orders/list"}
                status={0}
              />
            </Grid>
          </Grid>
        </div>

        <Stack
          direction="row"
          className={classes.countsContainer}
          sx={{
            justifyContent: "space-around",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" align="center">
              {t("Yritysprofiili avattu")}
            </Typography>
            <Typography variant="h2" align="center">
              {myCompany?.analytics?.company_info_opened_count?.count}{" "}
              {t("kertaa")}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" align="center">
              {t("Hiiltä kompensoitu")}
            </Typography>
            <Typography variant="h2" align="center">
              {myCompany?.analytics?.coal_compensated?.count
                ? myCompany?.analytics?.coal_compensated?.count
                : 0}
              t
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" align="center">
              {t("Ajoa suoritettu")}
            </Typography>
            <Typography variant="h2" align="center">
              {myCompany?.analytics?.drive_count?.count
                ? myCompany?.analytics?.drive_count?.count
                : 0}{" "}
              {t("kertaa")}
            </Typography>
          </Box>
        </Stack>
      </Fragment>
    );
  };

  const getDriverStart = () => {
    return (
      <Fragment>
        <div className={classes.root}>
          <Stack
            direction="column"
            className={classes.titleContainer}
            sx={{ justifyContent: "flex-start", alignItems: "center" }}
          >
            <Box sx={{ pb: 6 }}>
              <Typography variant="h2">
                {t("Tervetuloa OpenTaxi palveluun!")}
              </Typography>
            </Box>

            <Box sx={{ pb: 2 }}>
              <Typography variant="h3" align="center">
                {t(
                  "Kaikki kuljettajan palvelut ovat käytettävissä mobiilisovelluksessa.",
                )}
              </Typography>
            </Box>

            <Box sx={{ pb: 6 }}>
              <Typography variant="h3">
                {t("Lataa sovellus ja aloita palvelun käyttö tästä")}:
              </Typography>
            </Box>

            <Stack
              direction="row"
              sx={{
                justifyContent: "space-around",
                alignItems: "center",
                flexWrap: "wrap",
                width: "100%",
              }}
            >
              <Box>
                <a
                  href="https://apps.apple.com/us/app/opentaxi-tilaa-taksi/id1501555628?ls=1"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img border="0" src={appStoreLogo} alt="app store logo" />
                </a>
              </Box>
              <Box>
                <a
                  href="https://play.google.com/store/apps/details?id=com.opentaxi"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    border="0"
                    src={googlePlayLogo}
                    alt="google play logo"
                  />
                </a>
              </Box>
            </Stack>
          </Stack>
        </div>

        <Stack
          direction="row"
          className={classes.countsContainer}
          sx={{
            justifyContent: "space-around",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" align="center">
              {t("Vahvistettu kuljettajatili")}
            </Typography>
            <Typography variant="h2" align="center">
              {users?.data?.me?.verified ? (
                <Fragment>{myCompany?.name}</Fragment>
              ) : (
                <Fragment>{t("Ei vielä vahvistettu")}</Fragment>
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" align="center">
              {t("Liitetty ajoneuvo")}
            </Typography>
            <Typography variant="h2" align="center">
              {users?.data?.me?.car?.license_number
                ? users?.data?.me?.car?.license_number
                : "-"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" align="center">
              {t("Hiiltä kompensoitu")}
            </Typography>
            <Typography variant="h2" align="center">
              {myCompany?.analytics?.coal_compensated?.count
                ? myCompany?.analytics?.coal_compensated?.count
                : 0}
              t
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" align="center">
              {t("Ajoa suoritettu")}
            </Typography>
            <Typography variant="h2" align="center">
              {myCompany?.analytics?.drive_count?.count
                ? myCompany?.analytics?.drive_count?.count
                : 0}{" "}
              {t("kertaa")}
            </Typography>
          </Box>
        </Stack>
      </Fragment>
    );
  };

  return (
    <Fragment>
      {users?.data?.me?.status == 100 ? (
        <Fragment>{getCompanyStart()}</Fragment>
      ) : (
        <Fragment>{getDriverStart()}</Fragment>
      )}

      <Paper>
        <Box sx={{ p: 2 }}>
          <CompanyInfo company={myCompany} />
        </Box>
      </Paper>
    </Fragment>
  );
};

export default Start;
